import React, { useState } from "react";
import { Alert, Col, Form, Input, Radio, Row, Typography } from "antd";
import { DownOutlined } from "@ant-design/icons";
import { FormInstance } from "antd/es/form";
import {
  CustomRenderKey,
  IFieldConfig,
  ISectionConfig,
} from "./PublicClientDataRequestFormConfig";

const { Text } = Typography;

export interface IRendererStyles {
  textPrimary: string;
  textSecondary: string;
  borderLightColor: string;
  chipText: string;
  cardBg: string;
  errorColor?: string;
}

export type CustomRenderers = Partial<
  Record<CustomRenderKey, (config: IFieldConfig) => React.ReactNode>
>;

const requiredMark = (required?: boolean, errorColor?: string) =>
  required ? <span style={{ color: errorColor || "#ef4444" }}> *</span> : null;

const renderLabel = (
  config: IFieldConfig,
  textPrimary: string,
  errorColor?: string,
) => (
  <span style={{ fontSize: "13px", fontWeight: 500, color: textPrimary }}>
    {config.label}
    {requiredMark(config.required, errorColor)}
  </span>
);

/** Interactive Accordion Panel for Subsections */
const AccordionPanel: React.FC<{
  title: string;
  expanded: boolean;
  onToggle: () => void;
  children: React.ReactNode;
}> = ({ title, expanded, onToggle, children }) => {
  return (
    <div
      style={{
        border: "1px solid #e2e8f0",
        borderRadius: "12px",
        marginBottom: "14px",
        backgroundColor: "#ffffff",
        overflow: "hidden",
        boxShadow: "0 1px 3px rgba(0, 0, 0, 0.02)",
      }}
    >
      <div
        onClick={onToggle}
        style={{
          padding: "13px 18px",
          backgroundColor: expanded ? "#f1f5f9" : "#f8fafc",
          cursor: "pointer",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          userSelect: "none",
          borderBottom: expanded ? "1px solid #e2e8f0" : "none",
          transition: "background-color 0.2s ease-in-out",
        }}
      >
        <span
          style={{
            fontSize: "13.5px",
            fontWeight: 600,
            color: "#1e293b",
          }}
        >
          {title}
        </span>
        <DownOutlined
          style={{
            fontSize: "11.5px",
            color: "#64748b",
            transform: expanded ? "rotate(180deg)" : "rotate(0deg)",
            transition: "transform 0.25s ease-in-out",
          }}
        />
      </div>
      {expanded && (
        <div style={{ padding: "18px 18px 10px 18px" }}>
          <Row gutter={[24, 12]}>{children}</Row>
        </div>
      )}
    </div>
  );
};

/** Renders a single form field per its schema entry, or delegates to a named custom renderer. */
export const DynamicField: React.FC<{
  config: IFieldConfig;
  styles: IRendererStyles;
  customRenderers?: CustomRenderers;
}> = ({ config, styles, customRenderers }) => {
  return (
    <Form.Item shouldUpdate noStyle>
      {({ getFieldsValue }) => {
        if (config.visibleWhen && !config.visibleWhen(getFieldsValue(true))) {
          return null;
        }

        const colSpan = config.colSpan || { xs: 24, sm: 12 };
        const groupSpacing = config.groupStart ? "18px" : undefined;

        if (config.type === "custom") {
          const renderer =
            config.customKey && customRenderers?.[config.customKey];
          const showGenericLabel = config.customKey !== "dataImportFile";
          return (
            <Col
              xs={colSpan.xs}
              sm={colSpan.sm}
              style={{ marginTop: groupSpacing }}
            >
              {showGenericLabel && (
                <div
                  style={{
                    fontWeight: 600,
                    color: styles.textPrimary,
                    marginBottom: "6px",
                    fontSize: "13px",
                  }}
                >
                  {config.label}{" "}
                  {requiredMark(config.required, styles.errorColor)}
                </div>
              )}
              {config.required && (
                <Form.Item
                  name={config.name}
                  rules={[{ required: true, message: "Required" }]}
                  noStyle
                >
                  <Input type="hidden" />
                </Form.Item>
              )}
              {renderer ? renderer(config) : null}
            </Col>
          );
        }

        if (config.type === "hidden") {
          return (
            <Form.Item name={config.name} noStyle>
              <Input type="hidden" />
            </Form.Item>
          );
        }

        if (config.type === "subsection") {
          return (
            <Col
              xs={24}
              sm={24}
              style={{
                marginTop: config.groupStart ? "28px" : "8px",
                marginBottom: "12px",
              }}
            >
              <div
                style={{
                  fontSize: "13.5px",
                  fontWeight: 600,
                  color: "#1e293b",
                  textTransform: "none",
                  paddingBottom: "8px",
                  borderBottom: "1px solid #e2e8f0",
                }}
              >
                {config.label}
              </div>
            </Col>
          );
        }

        if (config.type === "info") {
          return (
            <Col
              xs={colSpan.xs}
              sm={colSpan.sm}
              style={{ marginTop: groupSpacing }}
            >
              <Alert
                type="info"
                showIcon
                message={config.label}
                description={config.helperText}
                style={{ borderRadius: "6px", marginBottom: "4px" }}
              />
            </Col>
          );
        }

        if (config.type === "radio") {
          const rules = config.required
            ? [{ required: true, message: "Required" }, ...(config.rules || [])]
            : config.rules;

          return (
            <Col
              xs={colSpan.xs}
              sm={colSpan.sm}
              style={{ marginTop: groupSpacing }}
            >
              <Form.Item
                name={config.name}
                label={renderLabel(
                  config,
                  styles.textPrimary,
                  styles.errorColor,
                )}
                initialValue={config.initialValue}
                rules={rules}
                style={{ marginBottom: "14px" }}
              >
                <Radio.Group
                  style={{
                    display: "flex",
                    flexDirection:
                      config.optionsLayout === "inline" ? "row" : "column",
                    flexWrap: "wrap",
                    gap: config.optionsLayout === "inline" ? "20px" : "8px",
                  }}
                >
                  {(config.options || [])
                    .filter(
                      (opt) =>
                        !opt.visibleWhen ||
                        opt.visibleWhen(getFieldsValue(true)),
                    )
                    .map((opt) => (
                      <Radio key={String(opt.value)} value={opt.value}>
                        {opt.label}
                      </Radio>
                    ))}
                </Radio.Group>
              </Form.Item>
            </Col>
          );
        }

        // text | email | phone | url
        const rules = [
          ...(config.required ? [{ required: true, message: "Required" }] : []),
          ...(config.rules || []),
        ];

        return (
          <Col
            xs={colSpan.xs}
            sm={colSpan.sm}
            style={{ marginTop: groupSpacing }}
          >
            <Form.Item
              name={config.name}
              label={renderLabel(config, styles.textPrimary, styles.errorColor)}
              rules={rules}
              style={{ marginBottom: "14px" }}
            >
              <Input
                size="middle"
                placeholder={config.placeholder}
                addonBefore={config.addonBefore}
                addonAfter={config.addonAfter}
                style={{ borderRadius: "6px" }}
              />
            </Form.Item>
            {config.helperText && (
              <Text
                type="secondary"
                style={{
                  display: "block",
                  fontSize: "12px",
                  marginTop: "-10px",
                  marginBottom: "4px",
                }}
              >
                {config.helperText}
              </Text>
            )}
          </Col>
        );
      }}
    </Form.Item>
  );
};

/** Renders a section's header + its fields. */
export const DynamicSection: React.FC<{
  config: ISectionConfig;
  styles: IRendererStyles;
  customRenderers?: CustomRenderers;
}> = ({ config, styles, customRenderers }) => {
  const [openAccordionIndex, setOpenAccordionIndex] = useState<number>(0);

  const hasSubsections =
    config.key === "contact_person" &&
    (config.fields || []).some((f) => f.type === "subsection");

  if (hasSubsections) {
    const groups: { title: string; fields: IFieldConfig[] }[] = [];
    let currentGroup: { title: string; fields: IFieldConfig[] } = {
      title: "General",
      fields: [],
    };

    (config.fields || []).forEach((field) => {
      if (field.type === "subsection") {
        if (currentGroup.fields.length > 0) {
          groups.push(currentGroup);
        }
        currentGroup = { title: field.label, fields: [] };
      } else {
        currentGroup.fields.push(field);
      }
    });
    if (currentGroup.fields.length > 0) {
      groups.push(currentGroup);
    }

    return (
      <div>
        {/* Fixed Sticky Section Header */}
        <div
          style={{
            position: "sticky",
            top: 0,
            backgroundColor: styles.cardBg,
            borderRadius: "14px 14px 0 0",
            zIndex: 10,
            padding: "20px 24px 14px 24px",
          }}
        >
          <div>
            <span
              style={{
                fontSize: "15px",
                fontWeight: 600,
                color: styles.textPrimary,
              }}
            >
              {config.label}
            </span>
          </div>

          {config.description ? (
            <p
              style={{
                color: styles.textSecondary,
                fontSize: "13px",
                margin: "6px 0 0 0",
              }}
            >
              {config.description}
            </p>
          ) : null}
        </div>

        {/* Scrollable Form Accordion Groups */}
        <div style={{ padding: "8px 24px 24px 24px" }}>
          {groups.map((group, idx) => {
            const isExpanded = openAccordionIndex === idx;
            return (
              <AccordionPanel
                key={group.title || idx}
                title={group.title}
                expanded={isExpanded}
                onToggle={() => setOpenAccordionIndex(isExpanded ? -1 : idx)}
              >
                {group.fields.map((field) => (
                  <DynamicField
                    key={field.name}
                    config={field}
                    styles={styles}
                    customRenderers={customRenderers}
                  />
                ))}
              </AccordionPanel>
            );
          })}
        </div>
      </div>
    );
  }

  return (
    <div>
      {/* Fixed Sticky Section Header */}
      <div
        style={{
          position: "sticky",
          top: 0,
          backgroundColor: styles.cardBg,
          borderRadius: "14px 14px 0 0",
          zIndex: 10,
          padding: "20px 24px 14px 24px",
        }}
      >
        <div>
          <span
            style={{
              fontSize: "15px",
              fontWeight: 600,
              color: styles.textPrimary,
            }}
          >
            {config.label}
          </span>
        </div>

        {config.description ? (
          <p
            style={{
              color: styles.textSecondary,
              fontSize: "13px",
              margin: "6px 0 0 0",
            }}
          >
            {config.description}
          </p>
        ) : null}
      </div>

      {/* Scrollable Form Content */}
      <div style={{ padding: "8px 24px 24px 24px" }}>
        <Row gutter={[24, 16]}>
          {(config.fields || []).map((field) => (
            <DynamicField
              key={field.name}
              config={field}
              styles={styles}
              customRenderers={customRenderers}
            />
          ))}
        </Row>
      </div>
    </div>
  );
};
